'use client';
import React, { createContext, useContext, useState, ReactNode } from 'react';

// Types for previous state information
export interface PreviousStateItem {
  vendorId: string;
  vendorName: string;
  previousState: string;
  apiSource: 'shoppinglist' | 'vendorAssessmentList' | 'prePanelList' | 'businessReviewPanelList' | 'tastingPanelList';
  listType: 'archived' | 'cancelled' | 'onhold';
  updatedAt: string; // ISO string timestamp
}

export interface PreviousStateContextType {
  previousStateList: PreviousStateItem[];
  updatePreviousStateFromResponse: (
    response: any,
    apiSource: PreviousStateItem['apiSource']
  ) => void;
  clearPreviousStateList: () => void;
  getPreviousStateByVendor: (vendorId: string) => PreviousStateItem | undefined;
  getPreviousStateByApiSource: (apiSource: PreviousStateItem['apiSource']) => PreviousStateItem[];
}

const PreviousStateContext = createContext<PreviousStateContextType | undefined>(undefined);

export const usePreviousStateContext = (): PreviousStateContextType => {
  const context = useContext(PreviousStateContext);
  if (!context) {
    throw new Error('usePreviousStateContext must be used within a PreviousStateProvider');
  }
  return context;
};

interface PreviousStateProviderProps {
  children: ReactNode;
}

export const PreviousStateProvider: React.FC<PreviousStateProviderProps> = ({ children }) => {
  const [previousStateList, setPreviousStateList] = useState<PreviousStateItem[]>([]);

  // Function to extract vendors from vendorStatus (for shoppinglist and vendorAssessmentList)
  const extractFromVendorStatus = (
    items: any[],
    listType: 'archived' | 'cancelled' | 'onhold',
    apiSource: PreviousStateItem['apiSource']
  ): PreviousStateItem[] => {
    const extractedItems: PreviousStateItem[] = [];
    
    items.forEach((item) => {
      if (Array.isArray(item.vendorStatus)) {
        item.vendorStatus.forEach((vendor: any) => {
         // if (vendor.previousState && vendor.id && vendor.vendorName) {
            extractedItems.push({
              vendorId: vendor.id,
              vendorName: vendor.vendorName,
              previousState: vendor.previousState,
              apiSource,
              listType,
              updatedAt: new Date().toISOString(),
            });
         // }
        });
      }
    });
    
    return extractedItems;
  };

  // Function to extract vendors from vendorDetails (for panel lists)
  const extractFromVendorDetails = (
    items: any[],
    listType: 'archived' | 'cancelled' | 'onhold',
    apiSource: PreviousStateItem['apiSource']
  ): PreviousStateItem[] => {
    const extractedItems: PreviousStateItem[] = [];
    
    items.forEach((item) => {
      if (Array.isArray(item.vendorDetails)) {
        item.vendorDetails.forEach((vendor: any) => {
       //   if (vendor.previousState && vendor.id && vendor.vendorName) {
            extractedItems.push({
              vendorId: vendor.id,
              vendorName: vendor.vendorName,
              previousState: vendor.previousState,
              apiSource,
              listType,
              updatedAt: new Date().toISOString(),
            });
        //  }
        });
      }
    });
    
    return extractedItems;
  };

  // Main function to update previous state from API response
  const updatePreviousStateFromResponse = (
    response: any,
    apiSource: PreviousStateItem['apiSource']
  ) => {
    const newPreviousStateItems: PreviousStateItem[] = [];
    const cancelledIdeas = response.cancelledIdeas ?? response.canceledIdeas;

    // Handle different API response structures
    if (apiSource === 'shoppinglist' || apiSource === 'vendorAssessmentList') {
      // For shopping list and vendor assessment, process archived/cancelled/onhold ideas
      if (response.archivedIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorStatus(response.archivedIdeas, 'archived', apiSource)
        );
      }
      if (cancelledIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorStatus(cancelledIdeas, 'cancelled', apiSource)
        );
      }
      if (response.onHoldIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorStatus(response.onHoldIdeas, 'onhold', apiSource)
        );
      }
    } else {
      // For panel lists (prePanelList, businessReviewPanelList, tastingPanelList)
      if (response.archivedIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorDetails(response.archivedIdeas, 'archived', apiSource)
        );
      }
      if (cancelledIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorDetails(cancelledIdeas, 'cancelled', apiSource)
        );
      }
      if (response.onHoldIdeas) {
        newPreviousStateItems.push(
          ...extractFromVendorDetails(response.onHoldIdeas, 'onhold', apiSource)
        );
      }
    }

    // Update state by removing existing items from same API source and adding new ones
    setPreviousStateList((prevList) => {
      // Remove existing items from the same API source to avoid duplicates
      const filteredList = prevList.filter((item) => item.apiSource !== apiSource);
      
      // Add new items
      return [...filteredList, ...newPreviousStateItems];
    });
  };

  // Function to clear all previous state data
  const clearPreviousStateList = () => {
    setPreviousStateList([]);
  };

  // Function to get previous state by vendor ID (returns most recent if multiple)
  const getPreviousStateByVendor = (vendorId: string): PreviousStateItem | undefined => {
    const vendorItems = previousStateList.filter((item) => item.vendorId === vendorId);
    if (vendorItems.length === 0) return undefined;
    
    // Return the most recent item
    return vendorItems.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
  };

  // Function to get previous state by API source
  const getPreviousStateByApiSource = (apiSource: PreviousStateItem['apiSource']): PreviousStateItem[] => {
    return previousStateList.filter((item) => item.apiSource === apiSource);
  };

  const contextValue: PreviousStateContextType = {
    previousStateList,
    updatePreviousStateFromResponse,
    clearPreviousStateList,
    getPreviousStateByVendor,
    getPreviousStateByApiSource,
  };

  return (
    <PreviousStateContext.Provider value={contextValue}>
      {children}
    </PreviousStateContext.Provider>
  );
};

export default PreviousStateContext;
